import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FilePreviewProvider, FilePreview, useFilePreview } from '@/common/components/FilePreview';
import DocumentsService from '@/services/documentsService';
import { Loader2 } from 'lucide-react';
import type { Document } from '@/types';
import toast from 'react-hot-toast';

interface PreviewDocumentDialogProps {
  document: Document | null;
  tramiteId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PreviewDocumentDialog: React.FC<PreviewDocumentDialogProps> = ({
  document,
  tramiteId,
  open,
  onOpenChange,
}) => {
  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90dvh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="truncate" title={document.name}>
            Previsualizador
          </DialogTitle>
          <DialogDescription className="sr-only">
            Vista previa del documento {document.name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <FilePreviewProvider>
            <PreviewContent document={document} tramiteId={tramiteId} open={open} />
          </FilePreviewProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Componente interno que maneja el FilePreview
const PreviewContent: React.FC<{ document: Document; tramiteId: number; open: boolean }> = ({
  document,
  tramiteId,
  open,
}) => {
  const { open: openPreview } = useFilePreview();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    const fetchAndPreview = async () => {
      if (!open) return;

      setIsLoading(true);
      setError(null);

      try {
        // Descargar documento con autenticación
        const blob = await DocumentsService.downloadDocument(tramiteId, document.id);

        if (!isMounted) return;

        // Crear blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Abrir preview con blob URL
        openPreview(blobUrl, document.name);
      } catch (err: any) {
        console.error('Error fetching document for preview:', err);
        if (isMounted) {
          setError('Error al cargar el documento para previsualización');
          toast.error('Error al cargar el documento');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAndPreview();

    return () => {
      isMounted = false;
    };
  }, [open, document.id, document.name, tramiteId, openPreview]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">Cargando archivo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return <FilePreview className="w-full h-full" />;
};
